import { useState, useEffect, useRef } from 'react';
import { BeatLoader } from 'react-spinners';
import { AiOutlineLoading, AiOutlineExclamationCircle, AiOutlineCheckCircle } from 'react-icons/ai';
import { BiBell } from 'react-icons/bi';
import { RiArrowUpSFill, RiArrowDownSFill } from 'react-icons/ri';
import { GrClose } from 'react-icons/gr';
import { HiCheckCircle, HiChevronDown, HiChevronUp, HiExclamationCircle, HiXCircle } from 'react-icons/hi';
import { BsChatLeftText } from 'react-icons/bs';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import ConfirmationModal from '../../../components/modal/ConfirmationModal';
import Modal from '../../../components/modal/modal';
import FPAlreadyExistsModal from '../../../components/modal/FPAlreadyExistsModal';
import Pagination from '../../../components/pagination';
import SortableHeader from '../../../components/SortableHeader';
import PortalSelector from '../../../components/PortalSelector';

// Table of Contents sections - defined outside component to avoid recreation
const TOC_SECTIONS = [
  { id: 'colors', title: '1. Farbpalette' },
  { id: 'typography', title: '2. Typografie' },
  { id: 'buttons', title: '3. Buttons' },
  { id: 'forms', title: '4. Formularelemente' },
  { id: 'tables', title: '5. Tabellen' },
  { id: 'badges', title: '6. Status Badges' },
  { id: 'info-boxes', title: '7. Info Boxen' },
  { id: 'loading', title: '8. Loading States' },
  { id: 'modals', title: '9. Modals' },
  { id: 'icons', title: '10. Icons' },
  { id: 'empty-state', title: '11. Empty State' },
  { id: 'pagination', title: '12. Pagination' },
  { id: 'sortable', title: '13. Sortierbare Tabellen-Header' },
  { id: 'portal', title: '14. Portal Selector' },
  { id: 'search', title: '15. Suche & Filter Patterns' },
  { id: 'accordion', title: '16. Accordion / Collapse' },
  { id: 'tabs', title: '17. Tabs' },
];

export default function AdminUIKit() {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Modal states for different modal types
  const [showBaseModal, setShowBaseModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showAnsprechpartnerModal, setShowAnsprechpartnerModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const totalCount = 50; // Example total

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // Portal selector state
  const [portal, setPortal] = useState('dgg');

  // Table sorting state
  const [tableSortKey, setTableSortKey] = useState('datum');
  const [tableSortAsc, setTableSortAsc] = useState(true);

  // Accordion state
  const [openAccordion, setOpenAccordion] = useState(1); // Section 1 is open by default

  // Tabs state
  const [activeTab, setActiveTab] = useState('overview');
  const [activePillTab, setActivePillTab] = useState('tab1');

  // Table of Contents state
  const [activeSection, setActiveSection] = useState('colors');
  const sectionRefs = useRef({});
  const containerRef = useRef(null);

  const handleTestAction = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setShowModal(false);
    }, 2000);
  };

  // Scroll to section
  const scrollToSection = (sectionId) => {
    const element = sectionRefs.current[sectionId];
    const container = containerRef.current;

    if (element && container) {
      // Calculate scroll position accounting for navbar
      const navbarHeight = 90;
      const scrollPosition = element.offsetTop - navbarHeight;

      container.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    }
  };

  // Track active section on scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Get all section IDs from TOC_SECTIONS
      const sectionIds = TOC_SECTIONS.map(s => s.id);

      let currentSection = sectionIds[0]; // Default to first section

      // Account for fixed navbar + some buffer
      const offset = 150; // Offset from top of viewport

      // Find the section that is currently most visible in viewport
      for (const sectionId of sectionIds) {
        const element = sectionRefs.current[sectionId];
        if (element) {
          const rect = element.getBoundingClientRect();

          // If the section top is above or at the offset line, it's the active one
          // We check sections in order, so the last one that passes becomes active
          if (rect.top <= offset) {
            currentSection = sectionId;
          }
        }
      }

      setActiveSection(currentSection);
    };

    container.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once on mount

    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const requestSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handleTableSort = (key) => {
    setTableSortAsc(tableSortKey === key ? !tableSortAsc : true);
    setTableSortKey(key);
  };

  return (
    <div ref={containerRef} className="dashboardContainer relative" style={{ overflowY: 'auto' }}>
      <h1 className="text-2xl font-bold mb-6 text-text text-left">UI Kit & Style Guide</h1>

      <div className="flex gap-6 items-start" style={{ minHeight: '100%' }}>
        {/* Main Content */}
        <div className="flex-1" style={{ minWidth: 0 }}>
          {/* Color System */}
          <div
            className="shadow-lg rounded-lg bg-white my-4 border border-borderBlue"
            style={{width: '100%'}}
          >
            <section ref={el => sectionRefs.current['colors'] = el} className="p-6 border-b border-borderBlue">
              <h2 className="text-xl font-semibold mb-4 text-text text-left">1. Farbpalette</h2>

          {/* Primary Colors */}
          <h3 className="text-lg font-semibold mb-3 text-text">Primärfarben</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div>
              <div className="h-20 rounded bg-mainBlue mb-2"></div>
              <p className="text-sm font-medium">mainBlue</p>
              <p className="text-xs text-grey">#3A46A9</p>
            </div>
            <div>
              <div className="h-20 rounded bg-lightBlue mb-2"></div>
              <p className="text-sm font-medium">lightBlue</p>
              <p className="text-xs text-grey">#4e57a6</p>
            </div>
            <div>
              <div className="h-20 rounded bg-borderBlue mb-2"></div>
              <p className="text-sm font-medium">borderBlue</p>
              <p className="text-xs text-grey">#c5c9f3</p>
            </div>
            <div>
              <div className="h-20 rounded bg-disableBlue mb-2"></div>
              <p className="text-sm font-medium">disableBlue</p>
              <p className="text-xs text-grey">#7d83b2</p>
            </div>
          </div>

          {/* Status Colors */}
          <h3 className="text-lg font-semibold mb-3 text-text">Statusfarben</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div>
              <div className="h-20 rounded bg-green mb-2"></div>
              <p className="text-sm font-medium">green</p>
              <p className="text-xs text-grey">#20A100</p>
            </div>
            <div>
              <div className="h-20 rounded bg-complete mb-2"></div>
              <p className="text-sm font-medium">complete</p>
              <p className="text-xs text-grey">#E2EFDA</p>
            </div>
            <div>
              <div className="h-20 rounded bg-red mb-2"></div>
              <p className="text-sm font-medium">red</p>
              <p className="text-xs text-grey">#E03C08</p>
            </div>
            <div>
              <div className="h-20 rounded bg-cancel mb-2"></div>
              <p className="text-sm font-medium">cancel</p>
              <p className="text-xs text-grey">#F8696B</p>
            </div>
          </div>

          {/* Warning Colors */}
          <h3 className="text-lg font-semibold mb-3 text-text">Warnfarben</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div>
              <div className="h-20 rounded bg-orange mb-2"></div>
              <p className="text-sm font-medium">orange</p>
              <p className="text-xs text-grey">#e8891a</p>
            </div>
            <div>
              <div className="h-20 rounded bg-orangeDark mb-2"></div>
              <p className="text-sm font-medium">orangeDark</p>
              <p className="text-xs text-grey">#ad7430</p>
            </div>
            <div>
              <div className="h-20 rounded bg-pending mb-2"></div>
              <p className="text-sm font-medium">pending</p>
              <p className="text-xs text-grey">#FEDC81</p>
            </div>
            <div>
              <div className="h-20 rounded bg-yellowLight mb-2"></div>
              <p className="text-sm font-medium">yellowLight</p>
              <p className="text-xs text-grey">#fcf3d3</p>
            </div>
          </div>

          {/* Neutral Colors */}
          <h3 className="text-lg font-semibold mb-3 text-text">Neutrale Farben</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="h-20 rounded bg-offWhite border border-borderBlue mb-2"></div>
              <p className="text-sm font-medium">offWhite</p>
              <p className="text-xs text-grey">#EBEAE9</p>
            </div>
            <div>
              <div className="h-20 rounded bg-lightgrey border border-borderBlue mb-2"></div>
              <p className="text-sm font-medium">lightgrey</p>
              <p className="text-xs text-grey">#f5f5f5</p>
            </div>
            <div>
              <div className="h-20 rounded bg-grey mb-2"></div>
              <p className="text-sm font-medium">grey</p>
              <p className="text-xs text-grey">#646464</p>
            </div>
            <div>
              <div className="h-20 rounded bg-text mb-2"></div>
              <p className="text-sm font-medium">text</p>
              <p className="text-xs text-grey">#282828</p>
            </div>
          </div>
        </section>

        {/* Typography */}
        <section ref={el => sectionRefs.current['typography'] = el} className="p-6 border-b border-borderBlue">
          <h2 className="text-xl font-semibold mb-4 text-text text-left">2. Typografie</h2>

          <div className="space-y-4">
            <div>
              <p className="text-xs text-grey mb-1">text-2xl font-bold (Page Heading)</p>
              <h1 className="text-2xl font-bold text-text">Seitentitel / Page Heading</h1>
            </div>
            <div>
              <p className="text-xs text-grey mb-1">text-xl font-semibold (Section Heading)</p>
              <h2 className="text-xl font-semibold text-text">Abschnittsüberschrift / Section Heading</h2>
            </div>
            <div>
              <p className="text-xs text-grey mb-1">text-lg font-semibold</p>
              <h3 className="text-lg font-semibold text-text">Unterüberschrift / Subheading</h3>
            </div>
            <div>
              <p className="text-xs text-grey mb-1">text-base (Body Text)</p>
              <p className="text-base text-text">Dies ist ein normaler Fließtext. This is regular body text.</p>
            </div>
            <div>
              <p className="text-xs text-grey mb-1">text-sm text-grey (Secondary Text)</p>
              <p className="text-sm text-grey">Sekundärer Text für zusätzliche Informationen.</p>
            </div>
            <div>
              <p className="text-xs text-grey mb-1">text-xs (Caption)</p>
              <span className="text-xs text-grey">Kleine Beschriftung oder Hinweis</span>
            </div>
          </div>
        </section>

        {/* Buttons */}
        <section ref={el => sectionRefs.current['buttons'] = el} className="p-6 border-b border-borderBlue">
          <h2 className="text-xl font-semibold mb-4 text-text text-left">3. Buttons</h2>

          <div className="space-y-6">
            {/* Primary Button */}
            <div>
              <h3 className="text-sm font-semibold mb-2 text-text">Primary Button</h3>
              <div className="flex gap-4 flex-wrap">
                <button className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all">
                  Normal
                </button>
                <button className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all">
                  <BiBell className="mr-2" size={16} />
                  Mit Icon
                </button>
                <button disabled className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all disabled:bg-disableBlue disabled:cursor-not-allowed">
                  Deaktiviert
                </button>
                <button className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all">
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Lädt...
                  </span>
                </button>
              </div>
            </div>

            {/* Secondary Button */}
            <div>
              <h3 className="text-sm font-semibold mb-2 text-text">Secondary Button (Grey)</h3>
              <div className="flex gap-4 flex-wrap">
                <button className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-grey hover:bg-text focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-grey transition-all">
                  Sekundär
                </button>
              </div>
            </div>

            {/* Success Button */}
            <div>
              <h3 className="text-sm font-semibold mb-2 text-text">Success Button</h3>
              <div className="flex gap-4 flex-wrap">
                <button className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-green hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green transition-all">
                  Bestätigen
                </button>
              </div>
            </div>

            {/* Destructive Button */}
            <div>
              <h3 className="text-sm font-semibold mb-2 text-text">Destructive Button</h3>
              <div className="flex gap-4 flex-wrap">
                <button className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-cancel hover:bg-red focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cancel transition-all">
                  Löschen
                </button>
              </div>
            </div>

            {/* Ghost Button */}
            <div>
              <h3 className="text-sm font-semibold mb-2 text-text">Ghost Button</h3>
              <div className="flex gap-4 flex-wrap">
                <button className="px-4 py-2 bg-whiteDark hover:bg-whiteDark text-grey rounded-md transition">
                  Abbrechen
                </button>
              </div>
            </div>

            {/* Text Button */}
            <div>
              <h3 className="text-sm font-semibold mb-2 text-text">Text Button</h3>
              <div className="flex gap-4 flex-wrap">
                <button className="text-cancel hover:underline">
                  Löschen
                </button>
                <button className="text-mainBlue hover:underline">
                  Bearbeiten
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Form Elements */}
        <section ref={el => sectionRefs.current['forms'] = el} className="p-6 border-b border-borderBlue">
          <h2 className="text-xl font-semibold mb-4 text-text text-left">4. Formularelemente</h2>

          <div className="space-y-6 max-w-2xl">
            {/* Text Input */}
            <div>
              <label className="block text-sm font-medium text-text mb-2">
                Text Input
              </label>
              <input
                type="text"
                placeholder="Platzhalter"
                className="w-full border border-borderBlue rounded px-3 py-2 text-text focus:border-mainBlue focus:outline-none"
              />
            </div>

            {/* Email Input */}
            <div>
              <label className="block text-sm font-medium text-text mb-2">
                E-Mail Input
              </label>
              <input
                type="email"
                placeholder="E-Mail-Adresse"
                className="w-full border border-borderBlue rounded px-3 py-2 text-text focus:border-mainBlue focus:outline-none"
              />
            </div>

            {/* Select Dropdown */}
            <div>
              <label className="block text-sm font-medium text-text mb-2">
                Select Dropdown
              </label>
              <select className="w-full border border-borderBlue rounded px-3 py-2 text-text focus:border-mainBlue focus:outline-none">
                <option value="1">Option 1</option>
                <option value="2">Option 2</option>
                <option value="3">Option 3</option>
              </select>
            </div>

            {/* Time Input */}
            <div>
              <label className="block text-sm font-medium text-text mb-2">
                Time Input
              </label>
              <input
                type="time"
                className="w-full border border-borderBlue rounded px-3 py-2 text-text focus:border-mainBlue focus:outline-none"
              />
            </div>

            {/* Two Column Grid */}
            <div>
              <h3 className="text-sm font-semibold mb-2 text-text">Two Column Grid</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Feld 1
                  </label>
                  <input
                    type="text"
                    className="w-full border border-borderBlue rounded px-3 py-2 text-text focus:border-mainBlue focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Feld 2
                  </label>
                  <input
                    type="text"
                    className="w-full border border-borderBlue rounded px-3 py-2 text-text focus:border-mainBlue focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tables */}
        <section ref={el => sectionRefs.current['tables'] = el} className="p-6 border-b border-borderBlue">
          <h2 className="text-xl font-semibold mb-4 text-text text-left">5. Tabellen</h2>

          <div className="space-y-8">
            {/* WV-Historie Table Pattern */}
            <div>
              <h3 className="text-lg font-semibold mb-3 text-text">WV-Historie Table Pattern</h3>
              <p className="text-sm text-grey mb-3">
                Verwendet in: WV-Historie, IC Mail History. Features: Row coloring, status icons, sortable headers mit Chevrons, filter inputs.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full bg-white rounded" style={{fontSize: "14px", tableLayout: 'fixed', borderCollapse: "separate", borderSpacing: '0 6px'}}>
                  <thead>
                    <tr className="bg-gray-100">
                      <th style={{width: '5%'}} className="px-1 py-2 text-center text-gray-700 font-semibold">
                        <span style={{lineHeight: "1.25", fontSize: "0.9rem"}}></span>
                      </th>
                      <th
                        onClick={() => handleTableSort('datum')}
                        style={{width: '15%'}}
                        className="px-1 py-2 text-center text-gray-700 font-semibold cursor-pointer select-none"
                      >
                        <div className="flex items-center justify-start gap-1">
                          <span style={{lineHeight: "1.25", fontSize: "0.9rem"}}>WV-Datum</span>
                          <div className="flex flex-col items-center leading-none">
                            <HiChevronUp className={`h-3 w-3 ${tableSortKey === 'datum' && tableSortAsc ? "text-black" : "text-whiteDark"}`} />
                            <HiChevronDown className={`h-3 w-3 ${tableSortKey === 'datum' && !tableSortAsc ? "text-black" : "text-whiteDark"}`} />
                          </div>
                        </div>
                        <input
                          className="w-full h-2 px-2 py-3 search mb-4"
                          type="text"
                          placeholder="Suche..."
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                      <th style={{width: '30%'}} className="px-1 py-2 text-center text-gray-700 font-semibold cursor-pointer" onClick={() => handleTableSort('firma')}>
                        <div className="flex items-center justify-start gap-1">
                          <span style={{lineHeight: "1.25", fontSize: "0.9rem"}}>Firma-Kurz</span>
                          <div className="flex flex-col items-center leading-none">
                            <HiChevronUp className={`h-3 w-3 ${tableSortKey === 'firma' && tableSortAsc ? "text-black" : "text-whiteDark"}`} />
                            <HiChevronDown className={`h-3 w-3 ${tableSortKey === 'firma' && !tableSortAsc ? "text-black" : "text-whiteDark"}`} />
                          </div>
                        </div>
                        <input
                          className="w-full h-2 px-2 py-3 search mb-4"
                          type="text"
                          placeholder="Suche..."
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                      <th style={{width: '20%'}} className="px-1 py-2 text-center text-gray-700 font-semibold">
                        <span style={{lineHeight: "1.25", fontSize: "0.9rem"}}>Berater</span>
                      </th>
                      <th style={{width: '30%'}} className="px-1 py-2 text-center text-gray-700 font-semibold">
                        <span style={{lineHeight: "1.25", fontSize: "0.9rem"}}>Bemerkung</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-complete rounded bg-opacity-30 my-2" style={{fontSize: "14px"}}>
                      <td className="px-1 py-2 text-center"><HiCheckCircle size={20} className="text-green font-bold"/></td>
                      <td className='p-1 text-left'>15.02.2025</td>
                      <td className='p-1 text-left' style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        <a href="#" className="text-mainBlue hover:underline">Musterfirma GmbH</a>
                      </td>
                      <td className='p-1 text-left'>Max Mustermann</td>
                      <td className='p-1 text-left' style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        <div className="flex items-center justify-between">
                          <span className="truncate">Follow-up Termin vereinbart</span>
                          <a href="#" className="text-mainBlue flex-shrink-0 ml-2" title="Details anzeigen">
                            <ArrowTopRightOnSquareIcon className="h-5 w-5"/>
                          </a>
                        </div>
                      </td>
                    </tr>
                    <tr className="bg-pending rounded bg-opacity-30 my-2" style={{fontSize: "14px"}}>
                      <td className="px-1 py-2 text-center"><HiExclamationCircle size={20} style={{color: "#FFA500"}}/></td>
                      <td className='p-1 text-left'>05.01.2025</td>
                      <td className='p-1 text-left' style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        <a href="#" className="text-mainBlue hover:underline">Beispiel AG</a>
                      </td>
                      <td className='p-1 text-left'>Anna Schmidt</td>
                      <td className='p-1 text-left' style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        <div className="flex items-center justify-between">
                          <span className="truncate">Rückruf erforderlich</span>
                          <a href="#" className="text-mainBlue flex-shrink-0 ml-2" title="Details anzeigen">
                            <ArrowTopRightOnSquareIcon className="h-5 w-5"/>
                          </a>
                        </div>
                      </td>
                    </tr>
                    <tr className="bg-cancel rounded bg-opacity-30 my-2" style={{fontSize: "14px"}}>
                      <td className="px-1 py-2 text-center"><HiXCircle size={20} className="text-red font-bold"/></td>
                      <td className='p-1 text-left'>10.01.2025</td>
                      <td className='p-1 text-left' style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        <a href="#" className="text-mainBlue hover:underline">Test Company</a>
                      </td>
                      <td className='p-1 text-left'>Peter Müller</td>
                      <td className='p-1 text-left' style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        <div className="flex items-center justify-between">
                          <span className="truncate">Störfall: System nicht erreichbar</span>
                          <a href="#" className="text-mainBlue flex-shrink-0 ml-2" title="Details anzeigen">
                            <ArrowTopRightOnSquareIcon className="h-5 w-5"/>
                          </a>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-3 p-4 bg-silver border border-borderBlue rounded">
                <p className="text-sm text-text mb-2">
                  <strong>Features:</strong>
                </p>
                <ul className="text-xs text-grey list-disc list-inside space-y-1">
                  <li>Row coloring: bg-complete (grün) für zukünftige Daten, bg-pending (gelb) für vergangene, bg-cancel (rot) für Störfälle</li>
                  <li>Status Icons in erster Spalte (HiCheckCircle, HiExclamationCircle, HiXCircle)</li>
                  <li>Sortierbare Header mit Chevron Icons (HiChevronUp/Down)</li>
                  <li>Filter-Inputs unter jedem Header</li>
                  <li>Fixed table layout mit definierten Spaltenbreiten</li>
                  <li>Text overflow ellipsis für lange Inhalte</li>
                  <li>Links mit ArrowTopRightOnSquareIcon zu Details</li>
                  <li>Separator spacing: borderCollapse: "separate", borderSpacing: '0 6px'</li>
                </ul>
              </div>
            </div>

            {/* Storfalle Table Pattern */}
            <div>
              <h3 className="text-lg font-semibold mb-3 text-text">Störfälle / Simple Table Pattern</h3>
              <p className="text-sm text-grey mb-3">
                Verwendet in: Störfälle, einfache Datenlisten. Features: Border-only design, tooltips, icon indicators.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full" style={{fontSize: "14px"}}>
                  <thead>
                    <tr className="border-y border-silver">
                      <th className="text-left py-2 text-gray-700 font-semibold w-40 pr-3">Firma Kurz</th>
                      <th className="text-left py-2 text-gray-700 font-semibold w-52 pr-3">Firma</th>
                      <th className="text-left py-2 text-gray-700 font-semibold w-16 pr-3">MA</th>
                      <th className="text-left py-2 text-gray-700 font-semibold w-40 pr-3">Zust. Berater</th>
                      <th className="text-left py-2 text-gray-700 font-semibold w-48 pr-3">P-Status</th>
                      <th className="text-left py-2 text-gray-700 font-semibold w-32 pr-3">P-Datum</th>
                      <th className="text-left py-2 text-gray-700 font-semibold w-32 pr-3">Störfall Datum</th>
                      <th className="text-left py-2 text-gray-700 font-semibold w-16 pr-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-y border-silver text-sm">
                      <td className="w-40 text-sm text-mainBlue underline font-light pr-3">
                        <a href="#" className="hover:text-opacity-80">Firma A</a>
                      </td>
                      <td className="w-52 text-gray-900 font-light pr-3">Firma A Vollständiger Name GmbH</td>
                      <td className="w-16 text-gray-900 font-light pr-3">AB</td>
                      <td className="w-40 text-gray-900 font-light pr-3">Max Mustermann</td>
                      <td className="w-48 text-gray-900 font-light pr-3">In Bearbeitung</td>
                      <td className="w-32 font-light text-gray-900 pr-3">15.01.2025</td>
                      <td className="w-32 font-light text-gray-900 pr-3">14.01.2025</td>
                      <td className="w-16 text-gray-900 font-light relative group">
                        <BsChatLeftText size='16px' className="cursor-help"/>
                        <span className="invisible group-hover:visible absolute z-10 bg-text text-white text-xs rounded py-1 px-2 right-0 bottom-full mb-1 w-48">
                          Server nicht erreichbar seit gestern
                        </span>
                      </td>
                    </tr>
                    <tr className="border-y border-silver text-sm">
                      <td className="w-40 text-sm text-mainBlue underline font-light pr-3">
                        <a href="#" className="hover:text-opacity-80">Firma B</a>
                      </td>
                      <td className="w-52 text-gray-900 font-light pr-3">Firma B Technologies AG</td>
                      <td className="w-16 text-gray-900 font-light pr-3">CD</td>
                      <td className="w-40 text-gray-900 font-light pr-3">Anna Schmidt</td>
                      <td className="w-48 text-gray-900 font-light pr-3">Eskaliert</td>
                      <td className="w-32 font-light text-gray-900 pr-3">10.01.2025</td>
                      <td className="w-32 font-light text-gray-900 pr-3">08.01.2025</td>
                      <td className="w-16 text-gray-900 font-light relative group">
                        <BsChatLeftText size='16px' className="cursor-help"/>
                        <span className="invisible group-hover:visible absolute z-10 bg-text text-white text-xs rounded py-1 px-2 right-0 bottom-full mb-1 w-48">
                          Datenbank Verbindungsfehler
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-3 p-4 bg-silver border border-borderBlue rounded">
                <p className="text-sm text-text mb-2">
                  <strong>Features:</strong>
                </p>
                <ul className="text-xs text-grey list-disc list-inside space-y-1">
                  <li>Einfaches border-y Design (oben und unten)</li>
                  <li>font-light für alle Zellen</li>
                  <li>Links mit text-mainBlue und underline</li>
                  <li>Tooltip mit BsChatLeftText Icon für Bemerkungen</li>
                  <li>Fixed width Spalten mit pr-3 padding</li>
                  <li>Hover-basierte Tooltips (group/group-hover Pattern)</li>
                </ul>
              </div>
            </div>

            {/* Table Patterns Summary */}
            <div className="p-4 bg-yellowLight border border-pending rounded">
              <h4 className="text-sm font-semibold mb-2 text-text">Table Pattern Richtlinien</h4>
              <ul className="text-xs text-orangeDark list-disc list-inside space-y-1">
                <li><strong>WV-Historie Pattern:</strong> Für Daten mit Status-Indikatoren, Sortierung und Filterung (WV-Historie, IC Mail History)</li>
                <li><strong>Simple Border Pattern:</strong> Für einfache Datenlisten ohne komplexe Interaktion (Störfälle, Übersichten)</li>
                <li><strong>Firmenprojekte Pattern:</strong> SortableHeader Component mit komplexer Sortierung (siehe Section 13)</li>
                <li><strong>Fixed Layout:</strong> Verwenden Sie tableLayout: 'fixed' für konsistente Spaltenbreiten</li>
                <li><strong>Text Overflow:</strong> whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"</li>
                <li><strong>Row Spacing:</strong> borderCollapse: "separate" mit borderSpacing für Lücken zwischen Zeilen</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Status Badges */}
        <section ref={el => sectionRefs.current['badges'] = el} className="p-6 border-b border-borderBlue">
          <h2 className="text-xl font-semibold mb-4 text-text text-left">6. Status Badges</h2>

          <div className="flex flex-wrap gap-4">
            <span className="px-2 py-1 rounded text-xs bg-complete text-green">
              Aktiv / Success
            </span>
            <span className="px-2 py-1 rounded text-xs bg-redLight text-red">
              Inaktiv / Error
            </span>
            <span className="px-2 py-1 rounded text-xs bg-yellowLight text-orangeDark">
              Ausstehend / Warning
            </span>
            <span className="px-2 py-1 rounded text-xs bg-silver text-text">
              Info
            </span>
            <span className="px-2 py-1 rounded text-xs bg-offWhite text-text border border-borderBlue">
              Neutral
            </span>
          </div>
        </section>

        {/* Info Boxes */}
        <section ref={el => sectionRefs.current['info-boxes'] = el} className="p-6 border-b border-borderBlue">
          <h2 className="text-xl font-semibold mb-4 text-text text-left">7. Info Boxen</h2>

          <div className="space-y-4">
            {/* Info Box */}
            <div className="p-4 bg-silver border border-borderBlue rounded">
              <p className="text-sm mb-2 text-text">
                <strong>Information:</strong> Dies ist eine Informationsbox.
              </p>
              <p className="text-sm text-grey">
                Zusätzliche beschreibende Informationen können hier angezeigt werden.
              </p>
            </div>

            {/* Warning Box */}
            <div className="p-4 bg-yellowLight border border-pending rounded">
              <p className="text-sm text-orangeDark">
                <strong>Hinweis:</strong> Dies ist eine Warnungsbox für wichtige Hinweise.
              </p>
            </div>

            {/* Error Box */}
            <div className="p-4 bg-redLight border border-borderRed rounded">
              <p className="text-sm text-red">
                <strong>Fehler:</strong> Dies ist eine Fehlerbox für Fehlermeldungen.
              </p>
            </div>

            {/* Success Box */}
            <div className="p-4 bg-complete border border-green rounded">
              <p className="text-sm text-green">
                <strong>Erfolg:</strong> Dies ist eine Erfolgsbox für positive Rückmeldungen.
              </p>
            </div>
          </div>
        </section>

        {/* Loading States */}
        <section ref={el => sectionRefs.current['loading'] = el} className="p-6 border-b border-borderBlue">
          <h2 className="text-xl font-semibold mb-4 text-text text-left">8. Loading States</h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold mb-2 text-text">BeatLoader</h3>
              <BeatLoader size={12} color={'#3A46A9'} />
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-2 text-text">Spinning Icon</h3>
              <AiOutlineLoading className="animate-spin text-mainBlue" size={24} />
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-2 text-text">Inline Spinner with Text</h3>
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4 text-mainBlue" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Wird geladen...
              </span>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-2 text-text">Centered Loading State</h3>
              <div className="flex justify-center py-8 bg-offWhite rounded">
                <BeatLoader size={12} color={'#3A46A9'} />
              </div>
            </div>
          </div>
        </section>

        {/* Modals */}
        <section ref={el => sectionRefs.current['modals'] = el} className="p-6 border-b border-borderBlue">
          <h2 className="text-xl font-semibold mb-4 text-text text-left">9. Modals</h2>

          <div className="space-y-8">
            {/* Base Modal */}
            <div>
              <h3 className="text-lg font-semibold mb-3 text-text">Base Modal (modal.js)</h3>
              <p className="text-sm text-grey mb-3">
                Generisches Modal mit Portal Rendering, ESC-Taste Support und Backdrop Click zum Schließen.
              </p>
              <button
                onClick={() => setShowBaseModal(true)}
                className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all"
              >
                Base Modal öffnen
              </button>
              <div className="mt-3 p-4 bg-silver border border-borderBlue rounded">
                <p className="text-sm text-text mb-2">
                  <strong>Props:</strong>
                </p>
                <code className="text-xs bg-white p-2 rounded block">
                  {`<Modal visible={true} onClose={handleClose}>\n  {children}\n</Modal>`}
                </code>
              </div>
            </div>

            {/* Confirmation Modal */}
            <div>
              <h3 className="text-lg font-semibold mb-3 text-text">Confirmation Modal</h3>
              <p className="text-sm text-grey mb-3">
                Bestätigungsdialog für Ja/Nein-Aktionen mit optionalem Icon, Loading State und konfigurierbaren Buttons.
              </p>
              <button
                onClick={() => setShowModal(true)}
                className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all"
              >
                Confirmation Modal öffnen
              </button>
              <div className="mt-3 p-4 bg-silver border border-borderBlue rounded">
                <p className="text-sm text-text mb-2">
                  <strong>Props:</strong>
                </p>
                <code className="text-xs bg-white p-2 rounded block">
                  {`<ConfirmationModal\n  visible={true}\n  title="Sind Sie sicher?"\n  message="Bitte bestätigen Sie Ihre Aktion."\n  confirmText="Ja"\n  cancelText="Nein"\n  onConfirm={handleConfirm}\n  onCancel={handleCancel}\n  loading={false}\n  fullWidthButtons={true}\n  icon={<AiOutlineExclamationCircle />}\n/>`}
                </code>
              </div>
            </div>

            {/* Info Modal */}
            <div>
              <h3 className="text-lg font-semibold mb-3 text-text">Info Modal (FPAlreadyExistsModal)</h3>
              <p className="text-sm text-grey mb-3">
                Einfaches Informations-Modal ohne Aktionen, nur zum Anzeigen von Nachrichten.
              </p>
              <button
                onClick={() => setShowInfoModal(true)}
                className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all"
              >
                Info Modal öffnen
              </button>
              <div className="mt-3 p-4 bg-silver border border-borderBlue rounded">
                <p className="text-sm text-text mb-2">
                  <strong>Verwendung:</strong> Bestehende FP-Warnung, Erfolgsmeldungen, Systemmeldungen
                </p>
                <code className="text-xs bg-white p-2 rounded block">
                  {`<FPAlreadyExistsModal\n  isOpen={true}\n  onClose={handleClose}\n  companyName="Beispiel AG"\n/>`}
                </code>
              </div>
            </div>

            {/* Selection Modal */}
            <div>
              <h3 className="text-lg font-semibold mb-3 text-text">Selection Modal (AnsprechPartnerModal)</h3>
              <p className="text-sm text-grey mb-3">
                Modal mit Dropdown-Auswahl, API-Integration, Loading States und dynamischen Optionen.
              </p>
              <button
                onClick={() => setShowAnsprechpartnerModal(true)}
                className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all"
              >
                Selection Modal öffnen
              </button>
              <div className="mt-3 p-4 bg-silver border border-borderBlue rounded">
                <p className="text-sm text-text mb-2">
                  <strong>Features:</strong>
                </p>
                <ul className="text-xs text-grey list-disc list-inside space-y-1">
                  <li>API-Aufruf beim Öffnen (sp_getResponsible, sp_getDataResponsibles)</li>
                  <li>Dropdown mit dynamischen Optionen</li>
                  <li>Loading State während API-Aufruf</li>
                  <li>Aktuelle Auswahl anzeigen</li>
                  <li>Speichern Button nur aktiv bei Änderung</li>
                </ul>
              </div>
            </div>

            {/* Form Modal */}
            <div>
              <h3 className="text-lg font-semibold mb-3 text-text">Form Modal (AddUsers, ChangePass)</h3>
              <p className="text-sm text-grey mb-3">
                Komplexes Formular-Modal mit Validation, React Hook Form Integration und mehreren Feldern.
              </p>
              <button
                onClick={() => setShowFormModal(true)}
                className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all"
              >
                Form Modal Beispiel öffnen
              </button>
              <div className="mt-3 p-4 bg-silver border border-borderBlue rounded">
                <p className="text-sm text-text mb-2">
                  <strong>Features:</strong>
                </p>
                <ul className="text-xs text-grey list-disc list-inside space-y-1">
                  <li>React Hook Form für Validation</li>
                  <li>Fehleranzeige unter Feldern</li>
                  <li>Required und Pattern Validation</li>
                  <li>Submit Button disabled bis Formular valid</li>
                  <li>Loading State beim Speichern</li>
                  <li>Toast Notifications für Erfolg/Fehler</li>
                </ul>
              </div>
            </div>

            {/* Modal Pattern Guidelines */}
            <div className="mt-6 p-4 bg-yellowLight border border-pending rounded">
              <h4 className="text-sm font-semibold mb-2 text-text">Modal Pattern Richtlinien</h4>
              <ul className="text-xs text-orangeDark list-disc list-inside space-y-1">
                <li><strong>Base Modal:</strong> Für eigene Modal-Implementierungen mit Custom Content</li>
                <li><strong>Confirmation Modal:</strong> Für alle Ja/Nein Bestätigungen, Löschaktionen</li>
                <li><strong>Info Modal:</strong> Für einfache Informationen ohne Nutzeraktion</li>
                <li><strong>Selection Modal:</strong> Für Dropdown/Liste Auswahl mit API-Daten</li>
                <li><strong>Form Modal:</strong> Für Datenerfassung mit Validation (User anlegen, Passwort ändern)</li>
                <li><strong>Complex Modal:</strong> ConsultantSelectionModal (progressive UI), DienstleistungenModal (nested components)</li>
              </ul>
            </div>

            {/* All Modal Files Reference */}
            <div className="mt-4 p-4 bg-silver border border-borderBlue rounded">
              <h4 className="text-sm font-semibold mb-2 text-text">Verfügbare Modal Komponenten</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-grey">
                <div>
                  <p className="font-medium text-text mb-1">Basis Modals:</p>
                  <ul className="list-disc list-inside">
                    <li>modal.js</li>
                    <li>ConfirmationModal.js</li>
                    <li>FPAlreadyExistsModal.js</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-text mb-1">Form Modals:</p>
                  <ul className="list-disc list-inside">
                    <li>addUsers.js</li>
                    <li>changePass.js</li>
                    <li>updateRole.js</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-text mb-1">Selection Modals:</p>
                  <ul className="list-disc list-inside">
                    <li>AnsprechPartnerModal.js</li>
                    <li>ConsultantSelectionModal.js</li>
                    <li>OpenAppointmentsModal.js</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-text mb-1">Complex Modals:</p>
                  <ul className="list-disc list-inside">
                    <li>ServiceMailModal.js</li>
                    <li>DienstleistungenModal.js</li>
                    <li>updateUpselling.js</li>
                    <li>BeratertageModal.js</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-text mb-1">Edit Modals:</p>
                  <ul className="list-disc list-inside">
                    <li>companyInfoPopUp.js</li>
                    <li>companyInfoPopUpEdit.js</li>
                    <li>companyInfoPopUpDGG.js</li>
                    <li>companyInfoPopUpDGGEdit.js</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Icons */}
        <section ref={el => sectionRefs.current['icons'] = el} className="p-6 border-b border-borderBlue">
          <h2 className="text-xl font-semibold mb-4 text-text text-left">10. Icons</h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold mb-3 text-text">Icon Größen</h3>
              <div className="flex items-center gap-6">
                <div className="flex flex-col items-center gap-2">
                  <BiBell size={16} className="text-mainBlue" />
                  <span className="text-xs text-grey">16px</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <BiBell size={20} className="text-mainBlue" />
                  <span className="text-xs text-grey">20px</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <BiBell size={24} className="text-mainBlue" />
                  <span className="text-xs text-grey">24px</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <BiBell size={28} className="text-mainBlue" />
                  <span className="text-xs text-grey">28px</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <BiBell size={32} className="text-mainBlue" />
                  <span className="text-xs text-grey">32px</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-3 text-text">Icon mit Text</h3>
              <div className="flex items-center gap-2 text-sm">
                <AiOutlineCheckCircle className="text-green" size={20} />
                <span className="text-text">Erfolgreich abgeschlossen</span>
              </div>
              <div className="flex items-center gap-2 text-sm mt-2">
                <AiOutlineExclamationCircle className="text-cancel" size={20} />
                <span className="text-text">Warnung oder Fehler</span>
              </div>
            </div>
          </div>
        </section>

        {/* Empty State */}
        <section ref={el => sectionRefs.current['empty-state'] = el} className="p-6 border-b border-borderBlue">
          <h2 className="text-xl font-semibold mb-4 text-text text-left">11. Empty State</h2>

          <div className="text-center py-16 bg-offWhite rounded">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-tahiti bg-opacity-10 rounded-full mb-4">
              <svg className="h-8 w-8 text-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-text mb-2">Keine Daten vorhanden</h3>
            <p className="text-base text-grey mb-6">
              Fügen Sie Daten hinzu, um loszulegen.
            </p>
            <button className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all">
              Daten hinzufügen
            </button>
          </div>
        </section>

        {/* Pagination */}
        <section ref={el => sectionRefs.current['pagination'] = el} className="p-6 border-b border-borderBlue">
          <h2 className="text-xl font-semibold mb-4 text-text text-left">12. Pagination</h2>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold mb-3 text-text">Standard Pagination</h3>
              <div className="flex justify-center py-4 bg-offWhite rounded">
                <Pagination
                  currentPage={currentPage}
                  totalCount={totalCount}
                  pageSize={pageSize}
                  onPageChange={page => setCurrentPage(page)}
                />
              </div>
            </div>

            <div className="p-4 bg-silver border border-borderBlue rounded">
              <p className="text-sm text-text mb-2">
                <strong>Verwendung:</strong>
              </p>
              <code className="text-xs bg-white p-2 rounded block">
                {`<Pagination\n  currentPage={currentPage}\n  totalCount={totalCount}\n  pageSize={pageSize}\n  onPageChange={page => setCurrentPage(page)}\n/>`}
              </code>
            </div>

            <div className="flex items-center gap-4 text-sm">
              <span className="text-grey">Aktuelle Seite:</span>
              <span className="font-semibold text-text">{currentPage}</span>
              <span className="text-grey">von {Math.ceil(totalCount / pageSize)}</span>
            </div>
          </div>
        </section>

        {/* Sortable Headers */}
        <section ref={el => sectionRefs.current['sortable'] = el} className="p-6 border-b border-borderBlue">
          <h2 className="text-xl font-semibold mb-4 text-text text-left">13. Sortierbare Tabellen-Header</h2>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold mb-3 text-text">SortableHeader Component</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-borderBlue">
                  <thead>
                    <tr className="bg-offWhite">
                      <th className="border border-borderBlue p-2 text-left">
                        <SortableHeader
                          label="Name"
                          field="name"
                          sortConfig={sortConfig}
                          requestSort={requestSort}
                        />
                      </th>
                      <th className="border border-borderBlue p-2 text-left">
                        <SortableHeader
                          label="E-Mail"
                          field="email"
                          sortConfig={sortConfig}
                          requestSort={requestSort}
                        />
                      </th>
                      <th className="border border-borderBlue p-2 text-left">
                        <SortableHeader
                          label="Datum"
                          field="date"
                          sortConfig={sortConfig}
                          requestSort={requestSort}
                        />
                      </th>
                      <th className="border border-borderBlue p-2 text-left text-text">
                        Aktionen
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="hover:bg-lightgrey">
                      <td className="border border-borderBlue p-2 text-text">Max Mustermann</td>
                      <td className="border border-borderBlue p-2 text-text">max@example.com</td>
                      <td className="border border-borderBlue p-2 text-text">15.01.2025</td>
                      <td className="border border-borderBlue p-2">
                        <button className="text-mainBlue hover:underline">Ansehen</button>
                      </td>
                    </tr>
                    <tr className="hover:bg-lightgrey">
                      <td className="border border-borderBlue p-2 text-text">Anna Schmidt</td>
                      <td className="border border-borderBlue p-2 text-text">anna@example.com</td>
                      <td className="border border-borderBlue p-2 text-text">14.01.2025</td>
                      <td className="border border-borderBlue p-2">
                        <button className="text-mainBlue hover:underline">Ansehen</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="p-4 bg-yellowLight border border-pending rounded">
              <p className="text-sm text-orangeDark">
                <strong>Hinweis:</strong> Klicken Sie auf die Pfeile in den Spaltenüberschriften zum Sortieren.
                Aktuelle Sortierung: {sortConfig.key ? `${sortConfig.key} (${sortConfig.direction})` : 'Keine'}
              </p>
            </div>
          </div>
        </section>

        {/* Portal Selector */}
        <section ref={el => sectionRefs.current['portal'] = el} className="p-6 border-b border-borderBlue">
          <h2 className="text-xl font-semibold mb-4 text-text text-left">14. Portal Selector</h2>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold mb-3 text-text">Portal Dropdown</h3>
              <PortalSelector
                portal={portal}
                setPortal={setPortal}
                superAdmin="1"
                role="i"
              />
            </div>

            <div className="p-4 bg-silver border border-borderBlue rounded">
              <p className="text-sm text-text mb-2">
                <strong>Aktuelles Portal:</strong> {portal === 'dgg' ? 'DGG' : 'R+V'}
              </p>
              <p className="text-sm text-grey">
                Der Portal Selector ist nur für Super Admins, Internal und Controller sichtbar.
              </p>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-3 text-text">Deaktivierter Zustand</h3>
              <PortalSelector
                portal={portal}
                setPortal={setPortal}
                superAdmin="1"
                role="i"
                disabled={true}
              />
            </div>
          </div>
        </section>

        {/* Search and Filter Patterns */}
        <section ref={el => sectionRefs.current['search'] = el} className="p-6 border-b border-borderBlue">
          <h2 className="text-xl font-semibold mb-4 text-text text-left">15. Suche & Filter Patterns</h2>

          <div className="space-y-6">
            {/* Search Input */}
            <div>
              <h3 className="text-sm font-semibold mb-3 text-text">Suchfeld</h3>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Suchen..."
                  className="w-full border border-borderBlue rounded px-4 py-2 pr-10 text-text focus:border-mainBlue focus:outline-none"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <svg className="h-5 w-5 text-grey" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Filter Buttons */}
            <div>
              <h3 className="text-sm font-semibold mb-3 text-text">Filter Buttons</h3>
              <div className="flex flex-wrap gap-2">
                <button className="px-4 py-2 text-sm border border-mainBlue text-mainBlue bg-white rounded hover:bg-mainBlue hover:text-white transition">
                  Alle
                </button>
                <button className="px-4 py-2 text-sm border border-borderBlue text-text bg-white rounded hover:bg-mainBlue hover:text-white hover:border-mainBlue transition">
                  Aktiv
                </button>
                <button className="px-4 py-2 text-sm border border-borderBlue text-text bg-white rounded hover:bg-mainBlue hover:text-white hover:border-mainBlue transition">
                  Inaktiv
                </button>
                <button className="px-4 py-2 text-sm border border-borderBlue text-text bg-white rounded hover:bg-mainBlue hover:text-white hover:border-mainBlue transition">
                  Ausstehend
                </button>
              </div>
            </div>

            {/* Date Range Filter */}
            <div>
              <h3 className="text-sm font-semibold mb-3 text-text">Datumsbereich Filter</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Von
                  </label>
                  <input
                    type="date"
                    className="w-full border border-borderBlue rounded px-3 py-2 text-text focus:border-mainBlue focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Bis
                  </label>
                  <input
                    type="date"
                    className="w-full border border-borderBlue rounded px-3 py-2 text-text focus:border-mainBlue focus:outline-none"
                  />
                </div>
                <div className="flex items-end">
                  <button className="w-full inline-flex items-center justify-center px-6 py-2 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all">
                    Filtern
                  </button>
                </div>
              </div>
            </div>

            {/* Active Filters Display */}
            <div>
              <h3 className="text-sm font-semibold mb-3 text-text">Aktive Filter</h3>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-mainBlue text-white rounded text-sm">
                  Status: Aktiv
                  <button className="hover:text-grey">×</button>
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-mainBlue text-white rounded text-sm">
                  Datum: 01.01.2025 - 31.01.2025
                  <button className="hover:text-grey">×</button>
                </span>
                <button className="px-3 py-1 text-sm text-cancel hover:underline">
                  Alle Filter löschen
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Accordion/Collapse */}
        <section ref={el => sectionRefs.current['accordion'] = el} className="p-6 border-b border-borderBlue">
          <h2 className="text-xl font-semibold mb-4 text-text text-left">16. Accordion / Collapse</h2>

          <div className="space-y-2">
            <div className="border border-borderBlue rounded">
              <button
                onClick={() => setOpenAccordion(openAccordion === 1 ? null : 1)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-lightgrey transition"
              >
                <span className="font-semibold text-text">Abschnitt 1</span>
                {openAccordion === 1 ? (
                  <RiArrowDownSFill className="text-grey" size={20} />
                ) : (
                  <RiArrowUpSFill className="text-grey" size={20} />
                )}
              </button>
              {openAccordion === 1 && (
                <div className="p-4 border-t border-borderBlue bg-white">
                  <p className="text-sm text-text">
                    Dies ist der Inhalt von Abschnitt 1. Hier können Sie zusätzliche Informationen oder
                    Formulare anzeigen.
                  </p>
                </div>
              )}
            </div>

            <div className="border border-borderBlue rounded">
              <button
                onClick={() => setOpenAccordion(openAccordion === 2 ? null : 2)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-lightgrey transition"
              >
                <span className="font-semibold text-text">Abschnitt 2</span>
                {openAccordion === 2 ? (
                  <RiArrowDownSFill className="text-grey" size={20} />
                ) : (
                  <RiArrowUpSFill className="text-grey" size={20} />
                )}
              </button>
              {openAccordion === 2 && (
                <div className="p-4 border-t border-borderBlue bg-white">
                  <p className="text-sm text-text">
                    Dies ist der Inhalt von Abschnitt 2. Accordion Sections können einzeln geöffnet
                    und geschlossen werden.
                  </p>
                </div>
              )}
            </div>

            <div className="border border-borderBlue rounded">
              <button
                onClick={() => setOpenAccordion(openAccordion === 3 ? null : 3)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-lightgrey transition"
              >
                <span className="font-semibold text-text">Abschnitt 3</span>
                {openAccordion === 3 ? (
                  <RiArrowDownSFill className="text-grey" size={20} />
                ) : (
                  <RiArrowUpSFill className="text-grey" size={20} />
                )}
              </button>
              {openAccordion === 3 && (
                <div className="p-4 border-t border-borderBlue bg-white">
                  <p className="text-sm text-text">
                    Dies ist der Inhalt von Abschnitt 3. Klicken Sie auf die Header, um zwischen
                    den Abschnitten zu wechseln.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Tabs */}
        <section ref={el => sectionRefs.current['tabs'] = el} className="p-6">
          <h2 className="text-xl font-semibold mb-4 text-text text-left">17. Tabs</h2>

          <div className="space-y-4">
            <div>
              <div className="border-b border-borderBlue">
                <nav className="flex space-x-8">
                  <button
                    onClick={() => setActiveTab('overview')}
                    className={`py-2 px-1 border-b-2 text-sm font-medium transition ${
                      activeTab === 'overview'
                        ? 'border-mainBlue text-mainBlue'
                        : 'border-transparent text-grey hover:text-text hover:border-grey'
                    }`}
                  >
                    Übersicht
                  </button>
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`py-2 px-1 border-b-2 text-sm font-medium transition ${
                      activeTab === 'details'
                        ? 'border-mainBlue text-mainBlue'
                        : 'border-transparent text-grey hover:text-text hover:border-grey'
                    }`}
                  >
                    Details
                  </button>
                  <button
                    onClick={() => setActiveTab('settings')}
                    className={`py-2 px-1 border-b-2 text-sm font-medium transition ${
                      activeTab === 'settings'
                        ? 'border-mainBlue text-mainBlue'
                        : 'border-transparent text-grey hover:text-text hover:border-grey'
                    }`}
                  >
                    Einstellungen
                  </button>
                </nav>
              </div>
              <div className="mt-4 p-4 bg-offWhite rounded">
                {activeTab === 'overview' && (
                  <div>
                    <h3 className="font-semibold text-text mb-2">Übersicht</h3>
                    <p className="text-text">Dies ist der Inhalt der Übersicht. Hier werden allgemeine Informationen angezeigt.</p>
                  </div>
                )}
                {activeTab === 'details' && (
                  <div>
                    <h3 className="font-semibold text-text mb-2">Details</h3>
                    <p className="text-text">Dies ist der Details-Bereich. Hier werden detaillierte Informationen angezeigt.</p>
                  </div>
                )}
                {activeTab === 'settings' && (
                  <div>
                    <h3 className="font-semibold text-text mb-2">Einstellungen</h3>
                    <p className="text-text">Dies ist der Einstellungs-Bereich. Hier können Konfigurationen vorgenommen werden.</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-3 text-text">Alternative Tab Stil (Pills)</h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => setActivePillTab('tab1')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    activePillTab === 'tab1'
                      ? 'bg-mainBlue text-white'
                      : 'bg-offWhite text-text hover:bg-lightgrey'
                  }`}
                >
                  Tab 1
                </button>
                <button
                  onClick={() => setActivePillTab('tab2')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    activePillTab === 'tab2'
                      ? 'bg-mainBlue text-white'
                      : 'bg-offWhite text-text hover:bg-lightgrey'
                  }`}
                >
                  Tab 2
                </button>
                <button
                  onClick={() => setActivePillTab('tab3')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    activePillTab === 'tab3'
                      ? 'bg-mainBlue text-white'
                      : 'bg-offWhite text-text hover:bg-lightgrey'
                  }`}
                >
                  Tab 3
                </button>
              </div>
              <div className="mt-4 p-4 bg-offWhite rounded">
                {activePillTab === 'tab1' && <p className="text-text">Inhalt von Tab 1</p>}
                {activePillTab === 'tab2' && <p className="text-text">Inhalt von Tab 2</p>}
                {activePillTab === 'tab3' && <p className="text-text">Inhalt von Tab 3</p>}
              </div>
            </div>
          </div>
        </section>
          </div>
        </div>

        {/* Table of Contents Sidebar */}
        <aside className="w-64 flex-shrink-0" style={{ height: 'fit-content', position: 'sticky', top: '90px', alignSelf: 'flex-start' }}>
          <div className="bg-white rounded-lg shadow-lg border border-borderBlue p-5">
            <h3 className="text-base font-bold mb-4 text-text border-b border-borderBlue pb-2">Inhaltsverzeichnis</h3>
            <div style={{maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', paddingRight: '4px'}}>
              {TOC_SECTIONS.map(section => {
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`
                      w-full text-left px-3 py-2 text-sm rounded-md transition-all mb-1 border-none cursor-pointer
                      ${isActive ? 'bg-mainBlue text-white font-semibold' : 'bg-transparent text-text font-normal hover:bg-lightgrey hover:text-mainBlue'}
                    `}
                  >
                    {section.title}
                  </button>
                );
              })}
            </div>
          </div>
        </aside>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={showModal}
        title="Aktion bestätigen"
        message="Möchten Sie diese Testaktion wirklich durchführen?"
        confirmText="Ja, fortfahren"
        cancelText="Abbrechen"
        onConfirm={handleTestAction}
        onCancel={() => setShowModal(false)}
        loading={loading}
        fullWidthButtons={true}
        icon={<AiOutlineExclamationCircle size={28} className="text-cancel" />}
      />

      {/* Base Modal */}
      <Modal visible={showBaseModal} onClose={() => setShowBaseModal(false)}>
        <h2 className="text-xl font-bold mb-4 text-text">Base Modal Beispiel</h2>
        <p className="text-base text-text mb-4">
          Dies ist ein einfaches Base Modal. Es verwendet Portal Rendering für korrektes z-Index Verhalten
          und unterstützt das Schließen via ESC-Taste oder Backdrop Click.
        </p>
        <div className="p-4 bg-offWhite rounded mb-4">
          <p className="text-sm text-grey mb-2">
            <strong>Features:</strong>
          </p>
          <ul className="text-sm text-grey list-disc list-inside space-y-1">
            <li>Portal Rendering mit createPortal</li>
            <li>ESC-Taste zum Schließen</li>
            <li>Backdrop Click zum Schließen</li>
            <li>Automatisches Event Cleanup</li>
            <li>X-Button in der Ecke</li>
          </ul>
        </div>
        <button
          onClick={() => setShowBaseModal(false)}
          className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all"
        >
          Modal schließen
        </button>
      </Modal>

      {/* Info Modal */}
      <FPAlreadyExistsModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        companyName="Beispiel Firma GmbH"
      />

      {/* Selection Modal Example */}
      {showAnsprechpartnerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-text bg-opacity-50">
          <div className="relative w-full max-w-lg bg-white p-6 rounded-lg shadow-lg mx-4">
            <GrClose
              className="cursor-pointer absolute top-4 right-4"
              size="22px"
              onClick={() => setShowAnsprechpartnerModal(false)}
            />

            <h2 className="text-xl font-bold mb-4">Ansprechpartner ändern</h2>

            <div className="mb-4">
              <p className="text-gray-700">
                Aktueller Ansprechpartner:{" "}
                <strong>Max Mustermann</strong>
              </p>
            </div>

            <div className="mb-4">
              <label className="block font-medium mb-1">
                Ansprechpartner auswählen
              </label>
              <select className="w-full p-2 border rounded border-borderBlue focus:border-mainBlue focus:outline-none">
                <option value="">Bitte wählen...</option>
                <option value="1">Anna Schmidt</option>
                <option value="2">Peter Müller</option>
                <option value="3">Lisa Weber</option>
              </select>
            </div>

            <div className="p-4 bg-yellowLight border border-pending rounded mb-4">
              <p className="text-xs text-orangeDark">
                <strong>Real-world Beispiel:</strong> AnsprechPartnerModal lädt Optionen via API (sp_getResponsible, sp_getDataResponsibles)
                und zeigt einen Loading State während des API-Aufrufs. Der Speichern Button ist nur aktiv wenn eine
                Änderung vorgenommen wurde.
              </p>
            </div>

            <button
              className="w-full py-2 px-4 rounded text-white bg-mainBlue hover:bg-opacity-90"
              onClick={() => setShowAnsprechpartnerModal(false)}
            >
              Ansprechpartner speichern
            </button>
          </div>
        </div>
      )}

      {/* Form Modal Example */}
      {showFormModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-text bg-opacity-50">
          <div className="relative w-full max-w-lg bg-white p-6 rounded-lg shadow-lg mx-4">
            <GrClose
              className="cursor-pointer absolute top-4 right-4"
              size="22px"
              onClick={() => setShowFormModal(false)}
            />

            <h2 className="text-xl font-bold mb-4 text-text">Formular Modal Beispiel</h2>

            <p className="text-sm text-grey mb-4">
              Dies zeigt das typische Pattern für Form Modals. Real-world Beispiele: AddUsers, ChangePass, UpdateRole.
            </p>

            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text mb-2">
                  E-Mail *
                </label>
                <input
                  type="email"
                  placeholder="E-Mail-Adresse"
                  className="w-full border border-borderBlue rounded px-3 py-2 text-text focus:border-mainBlue focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-2">
                  Passwort *
                </label>
                <input
                  type="password"
                  placeholder="Passwort"
                  className="w-full border border-borderBlue rounded px-3 py-2 text-text focus:border-mainBlue focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-2">
                  Rolle
                </label>
                <select className="w-full border border-borderBlue rounded px-3 py-2 text-text focus:border-mainBlue focus:outline-none">
                  <option value="1">Innendienst</option>
                  <option value="2">FKB</option>
                  <option value="3">VP</option>
                  <option value="4">Management</option>
                </select>
              </div>

              <div className="p-4 bg-yellowLight border border-pending rounded">
                <p className="text-xs text-orangeDark">
                  <strong>Hinweis:</strong> In echten Form Modals wird React Hook Form für Validation verwendet,
                  mit dynamischer Fehleranzeige und Submit Button Deaktivierung.
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  className="w-1/2 py-2 px-4 rounded text-white bg-mainBlue hover:bg-opacity-90"
                >
                  Speichern
                </button>
                <button
                  type="button"
                  onClick={() => setShowFormModal(false)}
                  className="w-1/2 py-2 px-4 rounded text-white bg-cancel hover:bg-red"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
